import { getErrorss } from '../../../../functions/errors/GetErrors'
import { getQueryOffers } from '../../../../functions/shared/api/queries/getQueryOffers'
import { fetchOffers } from '../../../../store/actionsCreators/offerActions/fetchOffers'
import { setUnitSold } from '../../../../store/actionsCreators/unitsActions'
import { dataFetchingFinish, dataFetchingStart } from '../../../../store/slices/modalSlice'
import { addOffer, offersSlice, setOffers } from '../../../../store/slices/offersSlice'
import { type AppDispatch } from '../../../../store/store'
import { ACCESS_KEY, urlAPI } from '../../../../urls'
import { buildManualOfferPayload } from './manualOfferPayload'
import { uploadManualOfferRelatedDocuments, uploadManualOfferRentDepositSlip, uploadManualOfferSignedDocument } from './manualOfferDocuments'
import { type ManualOfferFormValues, type ManualOfferListingType, type ManualOfferSubmitResult } from './manualOperations.types'

const extractOffer = (response: any): any | null => response?.response?.data ?? null

const updateManualOfferAfterUploads = (offer: any, documentIds: number[], typePatch: Record<string, any> = {}) => async (dispatch: AppDispatch): Promise<ManualOfferSubmitResult> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const currentDocumentIds = Array.isArray(offer?.attributes?.Documents?.data)
        ? offer.attributes.Documents.data.map((document: any) => Number(document?.id)).filter((id: number) => Number.isFinite(id))
        : Array.isArray(offer?.attributes?.Documents)
            ? offer.attributes.Documents.map((document: any) => Number(document?.id ?? document)).filter((id: number) => Number.isFinite(id))
        : []
    const mergedDocumentIds = Array.from(new Set([...currentDocumentIds, ...documentIds]))

    const response = await fetch(`${urlAPI}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
            method: 'PUT',
            collection: 'offers',
            id: Number(offer.id),
            query: getQueryOffers(),
            body: {
                ...offer.attributes,
                Type: [{ ...(offer?.attributes?.Type?.[0] ?? {}), ...typePatch }],
                Documents: mergedDocumentIds.map((id) => ({ id })),
                Notify: 'Agent'
            }
        })
    })
    const dataJSON = await response.json()
    const updatedOffer = extractOffer(dataJSON)
    if (dataJSON.success === true && updatedOffer != null) {
        dispatch(setOffers(updatedOffer))
        return { isSuccessful: true, offerId: Number(updatedOffer.id) }
    }
    return { isSuccessful: false, error: getErrorss(dataJSON), offerId: Number(offer?.id) }
}

export const createManualOffer = (values: ManualOfferFormValues) => async (dispatch: AppDispatch): Promise<ManualOfferSubmitResult> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY)

    try {
        dispatch(dataFetchingStart())

        const needsPassport = values.documentType === 'passport' || values.documentType === 'both'
        const needsEmiratesId = values.documentType === 'emirates-id' || values.documentType === 'both'

        if (needsPassport && values.documents.passport == null) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error: 'Passport is required.' }
        }
        if (needsEmiratesId && (values.documents.emiratesIdFront == null || values.documents.emiratesIdBack == null)) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error: 'Emirates ID front and back are required.' }
        }
        if (values.clientType === 'Company' && values.documents.tradeLicense == null) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error: 'Trade License is required for company offers.' }
        }
        if (values.unit.listingType === 'Sale' && values.documents.depositCheque == null) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error: 'Deposit Cheque is required for sale offers.' }
        }

        const bodyObject = {
            method: 'POST',
            collection: 'offers',
            query: getQueryOffers(),
            body: buildManualOfferPayload(values, [])
        }

        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            },
            body: JSON.stringify(bodyObject)
        })
        const dataJSON = await response.json()
        const offer = extractOffer(dataJSON)
        if (dataJSON.success !== true || offer == null) {
            const error = getErrorss(dataJSON)
            dispatch(offersSlice.actions.offersFetchingError({ message: error }))
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error }
        }

        dispatch(addOffer(offer))
        const uploadedDocumentIds: number[] = []
        const typePatch: Record<string, any> = {}

        const relatedDocuments = await dispatch(uploadManualOfferRelatedDocuments({
            offerId: Number(offer.id),
            documentType: values.documentType,
            documents: values.documents
        }))
        if (!relatedDocuments.isSuccessful) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error: relatedDocuments.error ?? 'Offer was created, but document upload failed.', offerId: Number(offer.id) }
        }
        uploadedDocumentIds.push(...(relatedDocuments.ids ?? []))

        if (values.documents.depositSlip != null && values.unit.listingType === 'Rent') {
            const depositSlip = await dispatch(uploadManualOfferRentDepositSlip({
                file: values.documents.depositSlip,
                offerId: Number(offer.id)
            }))
            if (!depositSlip.isSuccessful || depositSlip.url == null) {
                dispatch(dataFetchingFinish())
                return { isSuccessful: false, error: depositSlip.error ?? 'Offer was created, but deposit slip upload failed.', offerId: Number(offer.id) }
            }
            typePatch.DepositSlipLink = depositSlip.url
        }

        if (values.documents.signedDocument != null && values.unit.listingType != null) {
            const signedDocument = await dispatch(uploadManualOfferSignedDocument({
                file: values.documents.signedDocument,
                offerId: Number(offer.id),
                unitId: values.unit.unitId,
                listingType: values.unit.listingType as ManualOfferListingType
            }))
            if (!signedDocument.isSuccessful || signedDocument.id == null) {
                dispatch(dataFetchingFinish())
                return {
                    isSuccessful: false,
                    error: signedDocument.error ?? 'Offer was created, but signed document upload failed.',
                    offerId: Number(offer.id)
                }
            }
            uploadedDocumentIds.push(signedDocument.id)
        }

        const updateDocumentsResult = await dispatch(updateManualOfferAfterUploads(offer, uploadedDocumentIds, typePatch))
        if (!updateDocumentsResult.isSuccessful) {
            dispatch(dataFetchingFinish())
            return updateDocumentsResult
        }

        const soldResult = await dispatch(setUnitSold(values.unit.unitId, false, values.userId))
        if (soldResult?.isSuccessful !== true) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, error: 'Offer was created, but unit status could not be updated.', offerId: Number(offer.id) }
        }

        await dispatch(fetchOffers())
        dispatch(dataFetchingFinish())
        return { isSuccessful: true, offerId: Number(offer.id) }
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not create offer.'
        dispatch(offersSlice.actions.offersFetchingError({ message }))
        dispatch(dataFetchingFinish())
        return { isSuccessful: false, error: message }
    }
}
